import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface DealerFloatProps {
  onOpenPopup?: () => void;
  externalOpen?: boolean;
  externalTab?: 'login' | 'register';
  onExternalClose?: () => void;
  hideOnMobile?: boolean;
}

const DealerFloat = ({ onOpenPopup, externalOpen, externalTab, onExternalClose, hideOnMobile }: DealerFloatProps) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLikePopupOpen, setIsLikePopupOpen] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(16000);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'TR', label: 'TR', flag: '🇹🇷' },
    { code: 'EN', label: 'EN', flag: '🇬🇧' },
    { code: 'RU', label: 'RU', flag: '🇷🇺' },
    { code: 'AR', label: 'AR', flag: '🇸🇦' },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng.toLowerCase());
    setShowLangMenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (externalOpen) {
      setIsOpen(true);
      if (externalTab) setActiveTab(externalTab);
    }
  }, [externalOpen, externalTab]);

  const handleClose = () => {
    setIsOpen(false);
    if (onExternalClose) onExternalClose();
  };

  useEffect(() => {
    const handleOpenDealerPopup = () => {
      setIsOpen(true);
    };
    window.addEventListener('open-dealer-popup', handleOpenDealerPopup);
    return () => {
      window.removeEventListener('open-dealer-popup', handleOpenDealerPopup);
    };
  }, []);

  const handleLike = () => {
    if (!hasLiked) {
      setLikeCount((prev) => prev + 1);
      setHasLiked(true);
    }
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginData.username && loginData.password) {
      window.open('https://toptan.ticimedya.xyz', '_blank', 'noopener,noreferrer');
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('type', 'register');
      formDataToSend.append('companyName', registerData.companyName);
      formDataToSend.append('contactName', registerData.contactName);
      formDataToSend.append('email', registerData.email);
      formDataToSend.append('phone', registerData.phone);
      formDataToSend.append('city', registerData.city);
      formDataToSend.append('address', registerData.address);

      const response = await fetch('https://readdy.ai/api/form/d5u8i4kkjhtioh5jrkc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          setRegisterData({
            companyName: '',
            contactName: '',
            email: '',
            phone: '',
            city: '',
            address: '',
            password: '',
            confirmPassword: ''
          });
          setSubmitStatus('idle');
          setActiveTab('login');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Language Selector Button - Above Story Button */}
      <div
        ref={langMenuRef}
        className={`fixed left-0 z-50 ${hideOnMobile ? 'max-md:hidden' : ''}`}
        style={{ top: 'calc(50% - 140px)' }}
      >
        <motion.button
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          onClick={() => setShowLangMenu(!showLangMenu)}
          className="bg-gradient-to-r from-[#1a237e] to-[#0d1642] text-white px-1.5 py-2 rounded-r-lg shadow-xl hover:pl-2.5 transition-all duration-300 cursor-pointer group"
        >
          <div className="flex flex-col items-center gap-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-global-line text-base group-hover:scale-110 transition-transform duration-300"></i>
            </div>
            <span
              className="text-[10px] font-bold whitespace-nowrap"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              {i18n.language.toUpperCase()}
            </span>
          </div>
        </motion.button>

        {/* Language Dropdown */}
        <AnimatePresence>
          {showLangMenu && (
            <motion.div
              initial={{ opacity: 0, x: -10, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute left-full top-0 ml-2 bg-gradient-to-b from-[#0A1929] to-[#0D2137] rounded-xl shadow-2xl border border-[#D4AF37]/20 overflow-hidden"
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    i18n.language.toUpperCase() === lang.code
                      ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span>{lang.label}</span>
                  {i18n.language.toUpperCase() === lang.code && (
                    <i className="ri-check-line text-[#D4AF37] ml-auto"></i>
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Story Popup Button - Above Dealer Button */}
      {onOpenPopup && (
        <motion.button
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          onClick={onOpenPopup}
          className={`fixed left-0 z-50 bg-gradient-to-r from-[#1a237e] to-[#0d1642] text-white px-1.5 py-2 rounded-r-lg shadow-xl hover:pl-2.5 transition-all duration-300 cursor-pointer group ${hideOnMobile ? 'max-md:hidden' : ''}`}
          style={{ top: 'calc(50% - 70px)' }}
        >
          <div className="flex flex-col items-center gap-1">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-play-circle-line text-base group-hover:scale-110 transition-transform duration-300"></i>
            </div>
            <span className="text-[10px] font-bold whitespace-nowrap writing-vertical" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              {t('dealerFloat.intro').toUpperCase()}
            </span>
          </div>
        </motion.button>
      )}

      {/* Dealer Button - Left Side */}
      <motion.button
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        onClick={() => { setIsOpen(true); setActiveTab('login'); }}
        className={`fixed left-0 z-50 bg-gradient-to-r from-[#1a237e] to-[#0d1642] text-white px-1.5 py-2 rounded-r-lg shadow-xl hover:pl-2.5 transition-all duration-300 cursor-pointer group ${hideOnMobile ? 'max-md:hidden' : ''}`}
        style={{ top: 'calc(50% + 20px)' }}
      >
        <div className="flex flex-col items-center gap-1">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-store-3-line text-base group-hover:scale-110 transition-transform duration-300"></i>
          </div>
          <span className="text-[10px] font-bold whitespace-nowrap writing-vertical" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {t('dealerFloat.dealerLogin').toUpperCase()}
          </span>
        </div>
      </motion.button>

      {/* Like Button - Below Dealer Button */}
      <motion.button
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.5 }}
        onClick={() => setIsLikePopupOpen(true)}
        className={`fixed left-0 z-50 bg-gradient-to-r from-[#e91e63] to-[#c2185b] text-white px-1.5 py-2 rounded-r-lg shadow-xl hover:pl-2.5 transition-all duration-300 cursor-pointer group ${hideOnMobile ? 'max-md:hidden' : ''}`}
        style={{ top: 'calc(50% + 130px)' }}
      >
        <div className="flex flex-col items-center gap-1">
          <motion.div 
            className="w-4 h-4 flex items-center justify-center"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <i className="ri-heart-fill text-base"></i>
          </motion.div>
          <span className="text-[10px] font-bold whitespace-nowrap writing-vertical" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {t('dealerFloat.likes').toUpperCase()}
          </span>
        </div>
      </motion.button>

      {/* Like Popup Modal */}
      <AnimatePresence>
        {isLikePopupOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setIsLikePopupOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              className="bg-gradient-to-b from-[#0A1929] to-[#0D2137] rounded-2xl shadow-2xl w-full max-w-sm border border-[#D4AF37]/20 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with animated hearts */}
              <div className="relative bg-gradient-to-r from-[#e91e63] to-[#c2185b] p-8 overflow-hidden">
                {/* Floating hearts animation */}
                <div className="absolute inset-0 overflow-hidden">
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute text-white/30"
                      initial={{ 
                        x: Math.random() * 100 + '%', 
                        y: '100%',
                        scale: Math.random() * 0.5 + 0.5
                      }}
                      animate={{ 
                        y: '-100%',
                        rotate: Math.random() * 360
                      }}
                      transition={{ 
                        duration: Math.random() * 3 + 2,
                        repeat: Infinity,
                        delay: Math.random() * 2
                      }}
                    >
                      <i className="ri-heart-fill text-2xl"></i>
                    </motion.div>
                  ))}
                </div>

                <div className="relative text-center">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center"
                  >
                    <i className="ri-heart-fill text-white text-4xl"></i>
                  </motion.div>
                  <motion.h3 
                    className="text-white text-2xl font-bold"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    MARKAMIZI
                  </motion.h3>
                  <motion.div
                    className="flex items-center justify-center gap-2 mt-2"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <span className="text-white text-4xl font-black">{likeCount.toLocaleString('tr-TR')}</span>
                    <span className="text-white/90 text-xl font-semibold">KİŞİ</span>
                  </motion.div>
                  <motion.p
                    className="text-white text-xl font-bold mt-1"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    BEĞENDİ!
                  </motion.p>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setIsLikePopupOpen(false)}
                  className="absolute top-4 right-4 text-white/80 hover:text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 text-center">
                <p className="text-white/80 text-sm mb-6 leading-relaxed">
                  Kaliteli halılarımız ve müşteri memnuniyetimizle binlerce kişinin güvenini kazandık. 
                  <span className="text-[#D4AF37] font-semibold"> Siz de ailemize katılın!</span>
                </p>

                {/* Like Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLike}
                  disabled={hasLiked}
                  className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    hasLiked 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                      : 'bg-gradient-to-r from-[#e91e63] to-[#c2185b] text-white hover:shadow-lg hover:shadow-pink-500/30'
                  }`}
                >
                  {hasLiked ? (
                    <>
                      <motion.i 
                        className="ri-heart-fill text-2xl"
                        initial={{ scale: 0 }}
                        animate={{ scale: [0, 1.5, 1] }}
                        transition={{ duration: 0.5 }}
                      ></motion.i>
                      <span>Teşekkürler! Beğendiniz</span>
                    </>
                  ) : (
                    <>
                      <motion.i 
                        className="ri-heart-line text-2xl"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.8, repeat: Infinity }}
                      ></motion.i>
                      <span>Sende Beğen!</span>
                    </>
                  )}
                </motion.button>

                {/* Success animation */}
                {hasLiked && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 flex items-center justify-center gap-2 text-[#D4AF37]"
                  >
                    <i className="ri-sparkling-line text-xl"></i>
                    <span className="text-sm font-medium">Desteğiniz için teşekkür ederiz!</span>
                    <i className="ri-sparkling-line text-xl"></i>
                  </motion.div>
                )}

                {/* Social proof */}
                <div className="mt-6 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex -space-x-2">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div 
                          key={i} 
                          className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] border-2 border-[#0A1929] flex items-center justify-center"
                        >
                          <i className="ri-user-fill text-white text-xs"></i>
                        </div>
                      ))}
                    </div>
                    <span className="text-white/60 text-xs">+{(likeCount - 5).toLocaleString('tr-TR')} kişi beğendi</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => { if (!isSubmitting) handleClose(); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-gradient-to-b from-[#0A1929] to-[#0D2137] rounded-2xl shadow-2xl w-full max-w-md border border-[#D4AF37]/20 overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] p-6 relative">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <i className="ri-store-3-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white text-xl font-bold whitespace-nowrap">{t('dealerFloat.dealerPortal')}</h3>
                      <p className="text-white/80 text-sm">Hoş geldiniz</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-white hover:bg-white/20 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                {/* WhatsApp Contact */}
                <a
                  href="https://wa.me/902121234567"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-4 right-14 w-8 h-8 bg-[#25D366] rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer shadow-lg"
                >
                  <i className="ri-whatsapp-line text-white text-lg"></i>
                </a>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-white/10">
                <button
                  onClick={() => { setActiveTab('login'); setSubmitStatus('idle'); }}
                  className={`flex-1 py-4 text-sm font-medium transition-all duration-300 cursor-pointer ${
                    activeTab === 'login'
                      ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <i className="ri-login-box-line mr-2"></i>
                  {t('dealerFloat.dealerLogin')}
                </button>
                <button
                  onClick={() => { setActiveTab('register'); setSubmitStatus('idle'); }}
                  className={`flex-1 py-4 text-sm font-medium transition-all duration-300 cursor-pointer ${
                    activeTab === 'register'
                      ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <i className="ri-user-add-line mr-2"></i>
                  Bayi Kaydı
                </button>
              </div>

              {/* Login Form */}
              {activeTab === 'login' && (
                <form onSubmit={handleLoginSubmit} id="dealer-login-form" className="p-6 space-y-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      {t('dealerFloat.username')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-user-line text-[#D4AF37]"></i>
                      </div>
                      <input
                        type="text"
                        name="username"
                        required
                        value={loginData.username}
                        onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-12 pr-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="Kullanıcı adınız"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      {t('dealerFloat.password')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                        <i className="ri-lock-line text-[#D4AF37]"></i>
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        required
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-12 pr-12 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer"
                      >
                        <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37] cursor-pointer" />
                      <span className="text-white/70 group-hover:text-white transition-colors duration-300">{t('dealerFloat.rememberMe')}</span>
                    </label>
                    <a href="#" className="text-[#D4AF37] hover:text-[#B8941F] transition-colors duration-300">
                      {t('dealerFloat.forgotPassword')}
                    </a>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white font-bold py-3 rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-login-box-line"></i>
                    <span>{t('dealerFloat.login')}</span>
                  </button>
                </form>
              )}

              {/* Register Form */}
              {activeTab === 'register' && (
                <form onSubmit={handleRegisterSubmit} data-readdy-form id="dealer-register-form" className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">{t('dealerFloat.companyName')}</label>
                      <input
                        type="text"
                        name="companyName"
                        required
                        value={registerData.companyName}
                        onChange={(e) => setRegisterData({ ...registerData, companyName: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="Firma adı"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">{t('dealerFloat.authorizedName')}</label>
                      <input
                        type="text"
                        name="contactName"
                        required
                        value={registerData.contactName}
                        onChange={(e) => setRegisterData({ ...registerData, contactName: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="Ad Soyad"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">{t('dealerFloat.email')}</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                      placeholder="bayi@email.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">{t('dealerFloat.phone')}</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="+90 5XX XXX XX XX"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">{t('dealerFloat.city')}</label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={registerData.city}
                        onChange={(e) => setRegisterData({ ...registerData, city: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="İstanbul"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">{t('dealerFloat.address')}</label>
                    <textarea
                      name="address"
                      required
                      value={registerData.address}
                      onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                      rows={2}
                      maxLength={500}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300 resize-none"
                      placeholder="Açık adres"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">{t('dealerFloat.password')}</label>
                      <input
                        type="password"
                        name="password"
                        required
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">Şifre Tekrar</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        required
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#D4AF37] transition-all duration-300"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 flex items-center gap-2"
                    >
                      <i className="ri-checkbox-circle-line text-green-400"></i>
                      <span className="text-green-400 text-sm">Kayıt başarılı! Giriş yapabilirsiniz.</span>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2"
                    >
                      <i className="ri-error-warning-line text-red-400"></i>
                      <span className="text-red-400 text-sm">Kayıt başarısız! Şifreleri kontrol edin.</span>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white font-bold py-3 rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>Kaydediliyor...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line"></i>
                        <span>{t('dealerFloat.register')}</span>
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Footer Info */}
              <div className="px-6 pb-6">
                <div className="bg-white/5 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#25D366]/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-[#25D366] text-xl"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">Yardıma mı ihtiyacınız var?</p>
                    <a
                      href="https://wa.me/902121234567"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#25D366] text-sm font-medium hover:underline cursor-pointer"
                    >
                      WhatsApp ile iletişime geçin
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DealerFloat;
